import useStore from "../../../store"
import { Box, Field, Input, Textarea, Stack } from '@chakra-ui/react'
import { Radio, RadioGroup } from "../../ui/radio"
import { useState } from "react"
import { HappyButton, NeutralButton } from "../../common/Buttons"
import Spinner from "../../common/Spinner"
import SelectDropdown from "../../common/SelectDropdown"
import { typeEnum, purposeEnum, responseEnum } from "../../../constants/touchEnums"
import { colors } from "../../../utilities/colors"

export default function DealerNoteForm({ setShowNoteForm }) {
  const createDealerNote = useStore((state) => state.createDealerNote)
  const isCreatingDealerNote = useStore((state) => state.isCreatingDealerNote)
  const dealer = useStore((state) => state.dealer)

  const [formErrors, setFormErrors] = useState([])
  const [isATouch, setIsATouch] = useState(null)
  const [addReminder, setAddReminder] = useState(null)
  const [inputValues, setInputValues] = useState({
    touch: {
      touch_type: null,
      touch_purpose: null,
      touch_response: null,
    },
    subject: '',
    note: '',
    reminder: {
      reminder_due_on: null
    },
  })

  const handleSubmit = async () => {
    await createDealerNote(dealer.id, inputValues)
    setShowNoteForm(false)
  }

  const handleTouchChange = (e) => {
    setIsATouch(e.value)
    setInputValues((prevState) => ({
      ...prevState,
      touch: e.value === 'true' ? {touch_type: null, touch_purpose: null, touch_response: null} : null
    }))
  }

  return(
    <Box as="form" mt={2} ml={4} onSubmit={handleSubmit} dataTestId='dealer-note-form'>
      <Field.Root id="interaction" mb={2}>
        <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="interaction">
          Did you attempt to interact with the dealer?
        </Field.Label>
        <RadioGroup
          onValueChange={handleTouchChange}
          value={isATouch}
          colorPalette='gray'
        >
          <Stack direction="row" spacing={5}>
            <Radio
              value="true"
              _checked={{
                color: "white",
              }}
              >
                Yes
              </Radio>
            <Radio
              value="false"
              _checked={{
                color: "white",
              }}
            >
              No
            </Radio>
          </Stack>
        </RadioGroup>
      </Field.Root>

      {isATouch === 'true' &&
        <>
          <Field.Root id="type" mb={2} onClick={() => setFormErrors([])}>
            <SelectDropdown
              label="How did you interact with the dealer?"
              options={Object.keys(typeEnum)}
              defaultText={'Select type'}
              selectedItem={inputValues.touch.touch_type}
              setSelectedItem={(item) =>
                setInputValues((prevState) => ({
                  ...prevState,
                  touch: {
                    ...prevState.touch,
                    touch_type: item
                  }
                }))
              }
              displaySelectedItem={(item) => typeEnum[item]}
              isErrored={formErrors.includes('type')}
              testId="type-select"
            />
          </Field.Root>
          <Field.Root id="purpose" mb={2} onClick={() => setFormErrors([])}>
            <SelectDropdown
              label="What was the purpose of this interaction?"
              options={Object.keys(purposeEnum)}
              defaultText={'Select purpose'}
              selectedItem={inputValues.touch.touch_purpose}
              setSelectedItem={(item) =>
                setInputValues((prevState) => ({
                  ...prevState,
                  touch: {
                    ...prevState.touch,
                    touch_purpose: item
                  }
                }))
              }
              displaySelectedItem={(item) => purposeEnum[item]}
              isErrored={formErrors.includes('purpose')}
              testId="purpose-select"
            />
          </Field.Root>
          <Field.Root id="response" mb={2}>
            <SelectDropdown
              label="What was the resolution?"
              options={Object.keys(responseEnum)}
              defaultText={'Select response'}
              selectedItem={inputValues.touch.touch_response}
              setSelectedItem={(item) =>
                setInputValues((prevState) => ({
                  ...prevState,
                  touch: {
                    ...prevState.touch,
                    touch_response: item
                  }
                }))
              }
              displaySelectedItem={(item) => responseEnum[item]}
              isErrored={false}
              testId="response-select"
            />
          </Field.Root>
        </>
      }
      {isATouch &&
        <>
          <Field.Root id="subject" mb={2}>
            <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="subject">Subject</Field.Label>
            <Input
              value={inputValues.subject}
              onChange={(e) => setInputValues({ ...inputValues, subject: e.target.value })}
              bgColor={colors.grey900}
              _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
            />
          </Field.Root>
          <Field.Root id="note" mb={2}>
            <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="note">Note</Field.Label>
            <Textarea
              value={inputValues.touch?.note}
              onChange={(e) => setInputValues({ ...inputValues, note: e.target.value })}
              bgColor={colors.grey900}
              _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
            />
          </Field.Root>
        </>
      }
      {isATouch === 'true' &&
      <>
        <Field.Root id="add-reminder" mb={2} aria-labelledby="add-reminder">
          <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="add-reminder" id="add-reminder">
            Do you want to add a reminder?
          </Field.Label>
          <RadioGroup
            onValueChange={(e) => setAddReminder(e.value)}
            value={addReminder}
            data-testid='add-reminder-radio'
          >
            <Stack direction="row" spacing={5}>
              <Radio
                value="true"
                _checked={{
                  color: "white",
                }}
              >
                Yes
              </Radio>
              <Radio
                value="false"
                _checked={{
                  color: "white",
                }}
              >
                No
              </Radio>
            </Stack>
          </RadioGroup>
        </Field.Root>
        {addReminder === 'true' && (
          <>
            <Field.Root id="remind-me-on" mb={2}>
              <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="remind-me-on">
                Remind me on
              </Field.Label>
              <Input
                type="date"
                value={inputValues.reminder.reminder_due_on}
                onChange={(e) => setInputValues((prevState) => ({
                  ...prevState,
                  reminder: {
                    ...prevState.reminder,
                    reminder_due_on: e.target.value
                  }
                }))}
                bgColor={colors.grey900}
                _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
              />
            </Field.Root>
          </>
        )}
      </>
      }
      <div className="flex justify-evenly items-center">
        {isCreatingDealerNote ?
          <Spinner color="green" /> :
          <>
            <HappyButton
              text={'Submit'}
              onClick={handleSubmit}
              style="my-4 w-30 justify-items-center"
            />
            <NeutralButton
              text='Cancel'
              onClick={() => setShowNoteForm(false)}
              style="my-4 w-30 justify-items-center"
            />
          </>
        }
      </div>
    </Box>
  )
}